import React, { FunctionComponent, useState } from "react";
import {
  BannerConsumer,
  BannerPlaceholder,
  BannerProvider,
  Button,
  Checkbox,
  ColumnType,
  Dropdown,
  Form,
  IBannerContext,
  BannerStatus,
  TextInput
} from "@plex/react-components";
import {
  DataType,
  isObjectLike,
  isPrimitiveLike,
  isReferenceLike,
  nonPrimitiveTypesSelect,
  primitiveTypesSelect
} from "../NodeTypes/TypeDefinitions";
import { SchemaPicker } from "../NodePropertiesForm/SchemaPicker";
import { getSchema, getSchemaId, IListSchemasOptions } from "../NodeTypes/DataSchemas";
import { getSchemaObjectName } from "../FlowDocument/FlowDocumentModel";
import { IViewController, useViewController } from "../ViewContext";

export interface IUserDefinedSchemaPropertyDetailsFormProps {
  userDefinedSchemaId: string;
  initialState?: Partial<IUserDefinedSchemaPropertyDetailsFormState>;
  onCancel?: () => void;
  onSave?: (state: IUserDefinedSchemaPropertyDetailsFormState, bctx: IBannerContext) => void;
}

export interface IUserDefinedSchemaPropertyDetailsFormState {
  name: string;
  type: string;
  schemaId?: string;
  schemaName?: string;
  isList: boolean;
  required: boolean;
}

export const UserDefinedSchemaPropertyDetailsForm: FunctionComponent<IUserDefinedSchemaPropertyDetailsFormProps> = (
  props
) => {
  const { initialState, onCancel, onSave } = props;
  const { userDefinedSchemaDetailsDialogState }: IViewController = useViewController();
  const defaultType = primitiveTypesSelect[0]?.key || "string";

  const [formState, setFormState] = useState<IUserDefinedSchemaPropertyDetailsFormState>({
    name: initialState?.name ?? "",
    type: initialState?.type ?? defaultType,
    schemaId: initialState?.schemaId,
    schemaName: initialState?.schemaName,
    isList: initialState?.isList ?? false,
    required: initialState?.required ?? false
  });

  const objectSchemaPickerOptions: IListSchemasOptions = {
    includeLists: false,
    excludeReferences: true
  };

  const referenceSchemaPickerOptions: IListSchemasOptions = {
    includeLists: false,
    referenceOnly: true
  };

  const schemaPickerOnChange = (values: { id: string; name: string }[], bctx: IBannerContext) => {
    if (values.length > 0) {
      const newSchemaId = values[0]!.id;
      if (newSchemaId === props.userDefinedSchemaId) {
        bctx.addMessage("Property of object cannot be the object itself.", BannerStatus.warning);
        return;
      }
      setFormState({
        ...formState,
        schemaId: values[0]!.id,
        schemaName: values[0]!.name
      });
    } else {
      setFormState({
        ...formState,
        schemaId: undefined,
        schemaName: undefined
      });
    }
  };

  const handleSave = (bctx: IBannerContext) => {
    if (formState.type === DataType.OBJECT && !formState.schemaId) {
      bctx.addMessage("Please select an object.", BannerStatus.warning);
      return;
    }
    if (formState.type === DataType.OBJECTREFERENCE && !formState.schemaId) {
      bctx.addMessage("Please select a reference.", BannerStatus.warning);
      return;
    }
    if (onSave) {
      onSave(formState, bctx);
    }
  };

  return (
    <BannerProvider>
      <BannerConsumer>
        {(bctx: IBannerContext) => {
          return (
            <Form
              className="user-defined-schema-property-form"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                // Do not refresh the page when pressing enter!
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Form.Header>
                <BannerPlaceholder style={{ marginTop: "20px", marginBottom: 0, marginLeft: 0, marginRight: 0 }} />
              </Form.Header>
              <Form.Body>
                <Form.Section title="Property" layout={ColumnType.single}>
                  <Form.Row className="user-defined-schema-details-form-input-row">
                    <Form.FieldPair labelText="Name" required={true}>
                      <TextInput
                        data-testid="user-defined-schema-property-name"
                        value={formState.name}
                        disabled={true}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormState({
                            ...formState,
                            name: e.target.value
                          })
                        }
                        placeholder="Enter property name"
                      />
                    </Form.FieldPair>
                  </Form.Row>
                  <Form.Row className="user-defined-schema-details-form-input-row">
                    <Form.FieldPair labelText="Type" required={true}>
                      <Dropdown
                        data-testid="user-defined-schema-property-type"
                        items={nonPrimitiveTypesSelect.concat(primitiveTypesSelect)}
                        keySelector={(item: { [key: string]: string }) => item.key}
                        displaySelector={(item: { [key: string]: string }) => item.value}
                        selected={[{ key: formState.type, value: formState.type }]}
                        onSelectionChanged={(values) => {
                          if (values.length > 0) {
                            const selectedType = values[0]!.key;
                            let schemaId: string | undefined = undefined;
                            let schemaName: string | undefined = undefined;
                            if (isPrimitiveLike(selectedType as DataType)) {
                              schemaId = getSchemaId(selectedType as DataType);
                            }
                            if (
                              schemaId &&
                              (isObjectLike(selectedType as DataType) || isReferenceLike(selectedType as DataType))
                            ) {
                              const schema = getSchema(schemaId);
                              schemaName = getSchemaObjectName(schema);
                            }
                            setFormState({
                              ...formState,
                              type: values[0]!.key,
                              schemaId: schemaId,
                              schemaName: schemaName
                            });
                          }
                        }}
                      />
                    </Form.FieldPair>
                  </Form.Row>
                  {formState.type === DataType.OBJECT ? (
                    <Form.Row className="user-defined-schema-details-form-input-row">
                      <Form.FieldPair labelText="Object" required={true}>
                        <SchemaPicker
                          data-testid="user-defined-schema-property-object"
                          dialogTitle={"Document Schemas"}
                          initialSelection={
                            formState.schemaId
                              ? {
                                  id: formState.schemaId!,
                                  name: formState.schemaName ?? ""
                                }
                              : undefined
                          }
                          options={objectSchemaPickerOptions}
                          onChange={(values: { id: string; name: string }[]) => schemaPickerOnChange(values, bctx)}
                        />
                      </Form.FieldPair>
                    </Form.Row>
                  ) : (
                    <></>
                  )}
                  {formState.type === DataType.OBJECTREFERENCE ? (
                    <Form.Row className="user-defined-schema-details-form-input-row">
                      <Form.FieldPair labelText="Reference" required={true}>
                        <SchemaPicker
                          data-testid="user-defined-schema-property-reference"
                          dialogTitle={"Document Schemas"}
                          initialSelection={
                            formState.schemaId
                              ? {
                                  id: formState.schemaId!,
                                  name: formState.schemaName ?? ""
                                }
                              : undefined
                          }
                          options={referenceSchemaPickerOptions}
                          onChange={(values: { id: string; name: string }[]) => schemaPickerOnChange(values, bctx)}
                        />
                      </Form.FieldPair>
                    </Form.Row>
                  ) : (
                    <></>
                  )}
                  <Form.Row className="user-defined-schema-details-form-input-row">
                    <Form.FieldPair labelText="Schema">{/* TODO: Add schema picker */}</Form.FieldPair>
                    <Form.FieldPair labelText="Is List" required={true}>
                      <Checkbox
                        data-testid="user-defined-schema-property-is-list"
                        key={`Is List Input`}
                        checked={formState.isList}
                        onValueChange={(checked) => {
                          setFormState({
                            ...formState,
                            isList: checked
                          });
                        }}
                      />
                    </Form.FieldPair>
                    {!userDefinedSchemaDetailsDialogState.loopVariable && (
                      <Form.FieldPair labelText="Required" required={true}>
                        <Checkbox
                          data-testid="user-defined-schema-property-required"
                          key={`Required Input`}
                          checked={formState.required}
                          onValueChange={(checked) => {
                            setFormState({
                              ...formState,
                              required: checked
                            });
                          }}
                        />
                      </Form.FieldPair>
                    )}
                  </Form.Row>
                </Form.Section>
              </Form.Body>
              <Form.Footer>
                <Form.Row className="user-defined-schema-details-form-button-row">
                  <Button
                    data-testid="user-defined-schema-property-cancel"
                    variance={"link"}
                    className="user-defined-schema-details-form-secondary-button"
                    as={"button"}
                    type="button"
                    onClick={() => onCancel?.()}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="user-defined-schema-property-ok"
                    variance={"primary"}
                    as={"button"}
                    type="button"
                    disabled={!formState.name.trim()}
                    onClick={() => handleSave(bctx)}
                  >
                    Ok
                  </Button>
                </Form.Row>
              </Form.Footer>
            </Form>
          );
        }}
      </BannerConsumer>
    </BannerProvider>
  );
};
